import { collection, doc, getDocs, onSnapshot } from "firebase/firestore";
import { db } from "../../../../backend/firebaseConfig";
import formatUKPhone from "./formatUKPhone";

async function mapLimit(items, limit, mapper) {
  const results = new Array(items.length);
  let i = 0;

  async function worker() {
    while (i < items.length) {
      const idx = i++;
      results[idx] = await mapper(items[idx], idx);
    }
  }

  await Promise.all(
    Array.from({ length: Math.min(limit, items.length) }, worker),
  );
  return results;
}

export async function exportAdvisersData(subcollectionNames = [], callback) {
  try {
    const advisersRef = collection(
      db,
      "Authorized Members",
      "Advisers",
      "Data",
    );
    const advisersSnap = await getDocs(advisersRef);

    const docs = advisersSnap.docs;

    // Tune this: 8–15 is usually a good balance for Firestore
    const CONCURRENCY = 10;

    const formatted = await mapLimit(docs, CONCURRENCY, async (docSnap) => {
      const baseData = { id: docSnap.id, ...docSnap.data() };
      if (
        baseData?.isAppointed === false ||
        baseData?.isIndroducerAR === true
      ) {
        return null;
      }

      const subResults = Object.fromEntries(
        await Promise.all(
          subcollectionNames.map(async (sub) => {
            try {
              const subRef = collection(
                db,
                "Authorized Members",
                "Advisers",
                "Data",
                docSnap.id,
                sub,
              );
              const subSnap = await getDocs(subRef);
              return [
                sub,
                subSnap.docs.map((d) => ({ id: d.id, ...d.data() })),
              ];
            } catch {
              return [sub, []];
            }
          }),
        ),
      );

      const personal = subResults["Personal Details"]?.[0] || {};

      return {
        id: docSnap.id,
        firstName: personal?.forename || baseData?.firstName || "",
        preferredName: personal?.preferredName || baseData?.preferredName || "",
        lastName: personal?.surname || baseData?.lastName || "",
        email: baseData?.email || docSnap.id || "-",
        status: baseData?.status || [],
        tradingStyles: baseData?.tradingStyles || [],
        mobileNumber:
          formatUKPhone(personal?.mobileTelephone) ||
          formatUKPhone(personal?.homeTelephone) ||
          "",
        officeNumber: formatUKPhone(personal?.officeTelephone) || "",
        fcaNumber: personal?.fcaNumber || baseData?.fcaNumber || "",
        businessAddress: personal?.businessAddress?.fullAddress || "",
        tradingStyle: baseData?.tradingStyle || "",
        tradingName: baseData?.tradingName || "",
        isAR: !!baseData?.isAR,
        isIndroducerAR: !!baseData?.isIndroducerAR,
      };
    });

    const cleaned = formatted.filter(Boolean);

    if (typeof callback === "function") await callback(cleaned);
    return cleaned;
  } catch {
    if (typeof callback === "function") await callback([]);
    return [];
  }
}

const formatContact = (id, baseData = {}, personal = {}) => ({
  id,
  firstName: personal?.forename || baseData?.firstName || "",
  preferredName: personal?.preferredName || baseData?.preferredName || "",
  lastName: personal?.surname || baseData?.lastName || "",
  email: baseData?.email || id || "-",
  status: baseData?.status || [],
  tradingStyles: baseData?.tradingStyles || [],
  mobileNumber:
    formatUKPhone(personal?.mobileTelephone) ||
    formatUKPhone(personal?.homeTelephone) ||
    "",
  officeNumber: formatUKPhone(personal?.officeTelephone) || "",
  fcaNumber: personal?.fcaNumber || baseData?.fcaNumber || "",
  businessAddress: personal?.businessAddress?.fullAddress || "",
  tradingStyle: baseData?.tradingStyle || "",
  tradingName: baseData?.tradingName || "",
  isAR: !!baseData?.isAR,
  isIndroducerAR: !!baseData?.isIndroducerAR,
});

export const subscribeContactListData = (onValue, onError) => {
  if (typeof onValue !== "function") return () => {};

  const entries = new Map();
  let adviserOrder = [];
  let stopped = false;

  const emit = () => {
    if (stopped) return;

    const includedEntries = adviserOrder
      .map((id) => entries.get(id))
      .filter(
        (entry) =>
          entry &&
          entry.base?.isAppointed !== false &&
          entry.base?.isIndroducerAR !== true,
      );

    if (includedEntries.some((entry) => !entry.personalLoaded)) return;

    onValue(
      includedEntries.map((entry) =>
        formatContact(entry.id, entry.base, entry.personal),
      ),
    );
  };

  const stopEntry = (entry) => {
    entry?.unsubscribePersonal?.();
  };

  const unsubscribeAdvisers = onSnapshot(
    collection(db, "Authorized Members", "Advisers", "Data"),
    (snapshot) => {
      const currentIds = new Set(snapshot.docs.map((item) => item.id));

      for (const [id, entry] of entries) {
        if (!currentIds.has(id)) {
          stopEntry(entry);
          entries.delete(id);
        }
      }

      adviserOrder = snapshot.docs.map((item) => item.id);

      snapshot.docs.forEach((item) => {
        const base = item.data() || {};
        let entry = entries.get(item.id);

        if (!entry) {
          entry = {
            id: item.id,
            base,
            personal: {},
            personalLoaded: false,
            unsubscribePersonal: null,
          };
          entries.set(item.id, entry);

          entry.unsubscribePersonal = onSnapshot(
            doc(
              db,
              "Authorized Members",
              "Advisers",
              "Data",
              item.id,
              "Personal Details",
              "Data",
            ),
            (personalSnapshot) => {
              entry.personal = personalSnapshot.exists()
                ? personalSnapshot.data()
                : {};
              entry.personalLoaded = true;
              emit();
            },
            (error) => {
              entry.personalLoaded = true;
              emit();
              if (!stopped && typeof onError === "function") onError(error);
            },
          );
        } else {
          entry.base = base;
        }
      });

      emit();
    },
    (error) => {
      if (!stopped && typeof onError === "function") onError(error);
    },
  );

  return () => {
    stopped = true;
    unsubscribeAdvisers();
    entries.forEach(stopEntry);
    entries.clear();
  };
};
